"use client";

import { Info } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { LockedDnmStatusSection } from "@/components/features/dao/voting/LockedDnmStatusSection";
import { useCurrentCycle } from "@/hooks/dao/useCurrentCycle";
import { useVotingPeriod } from "@/hooks/dao/useVotingPeriod";
import { BeCandidateButton } from "./BeCandidateButton";
import { CandidatesSkeleton } from "./CandidatesSkeleton";
import { CycleBanner } from "./CycleBanner";
import { ElectionListSection } from "./ElectionListSection";

/**
 * Election tab — top of the DAO page.
 *
 * Layout:
 *   1. CycleBanner — count-down + cycle status.
 *   2. Be-candidate + راهنما toggle row.
 *   3. LockedDnmStatusSection — always rendered; switches between deposited /
 *      withdraw / guide variants internally.
 *   4. ElectionListSection — owns the (kind, cycle) view state and the year /
 *      mode dropdown picker (Figma 5462:51506 + 5462:51589). Defaults to live
 *      voting view while voting is active; flips to "members of current
 *      cycle" once voting ends, with the dropdown letting the user browse
 *      historical members + candidates back to year 1.
 */
export const ElectionTab: React.FC = () => {
  const t = useTranslations("dao");
  const { cycle } = useCurrentCycle();
  // cycleEnded is chain-time-anchored — the browser clock can disagree with
  // the chain (Hardhat fork warps, NTP drift), so deriving from `Date.now()`
  // here would mis-flip the UI on test rigs.
  const { isLoading, cycleEnded } = useVotingPeriod(cycle);
  // "راهنما" toggle: when true, LockedDnmStatusSection swaps its
  // WithdrawDnmCard for the guide info paragraph (same slot, either-or).
  const [showGuide, setShowGuide] = useState(false);

  if (isLoading) {
    return <CandidatesSkeleton />;
  }

  return (
    <div className="flex flex-col gap-4">
      <CycleBanner />
      <div className="flex justify-between items-center">
        <BeCandidateButton />
        <button
          type="button"
          onClick={() => setShowGuide((prev) => !prev)}
          aria-label={t("election.tab.guide")}
          aria-pressed={showGuide}
          className="flex items-center gap-1 text-xs font-medium text-text-secondary hover:text-text-primary transition-colors cursor-pointer"
        >
          <Info className="size-4" aria-hidden="true" />
          {t("election.tab.guide")}
        </button>
      </div>
      <LockedDnmStatusSection showGuide={showGuide} />
      <ElectionListSection
        currentCycle={cycle ?? 0}
        cycleEnded={cycleEnded ?? false}
      />
    </div>
  );
};
